import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "@/components/ScrollToTop";
import { lazy, Suspense } from "react";

// Layout (keep synchronous for shell)
import Layout from "@/components/layout/Layout";
import AdminLayout from "@/components/layout/AdminLayout";

// Loading component for suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ht-red"></div>
  </div>
);

// Lazy load public pages
const HomePage = lazy(() => import("@/pages/HomePage"));
const AboutUsPage = lazy(() => import("@/pages/AboutUsPage"));
const LeadershipPage = lazy(() => import("@/pages/LeadershipPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const NextStepsPage = lazy(() => import("@/pages/NextStepsPage"));
const NextStepsHubPage = lazy(() => import("@/pages/NextStepsHubPage"));
const PlanOfSalvationPage = lazy(() => import("@/pages/PlanOfSalvationPage"));
const BaptismPage = lazy(() => import("@/pages/BaptismPage"));
const MembershipPage = lazy(() => import("@/pages/MembershipPage"));
const PartnershipPage = lazy(() => import("@/pages/PartnershipPage"));
const PrayerRequestPage = lazy(() => import("@/pages/PrayerRequestPage"));
const MinistriesPage = lazy(() => import("@/pages/MinistriesPage"));
const MinistryDetailPage = lazy(() => import("@/pages/MinistryDetailPage"));
const EventsPage = lazy(() => import("@/pages/EventsPage"));
const EventDetailPage = lazy(() => import("@/pages/EventDetailPage"));
const SermonsPage = lazy(() => import("@/pages/SermonsPage"));
const SermonDetailPage = lazy(() => import("@/pages/SermonDetailPage"));
const GivingOptionsPage = lazy(() => import("@/pages/GivingOptionsPage"));
const PlanYourVisitPage = lazy(() => import("@/pages/PlanYourVisitPage"));
const VolunteerPage = lazy(() => import("@/pages/VolunteerPage"));
const ShareTestimonyPage = lazy(() => import("@/pages/ShareTestimonyPage"));
const LiveWatchPage = lazy(() => import("@/pages/LiveWatchPage"));
const ResourcesPage = lazy(() => import("@/pages/ResourcesPage"));
const ResourceViewerPage = lazy(() => import("@/pages/ResourceViewerPage"));
const ConnectCardPage = lazy(() => import("@/pages/ConnectCardPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("@/pages/TermsOfServicePage"));
const SafetyPolicyPage = lazy(() => import("@/pages/SafetyPolicyPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

// Lazy load admin pages
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const ForgotPassword = lazy(() => import("@/pages/admin/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/admin/ResetPassword"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminSubmissions = lazy(() => import("@/pages/admin/AdminSubmissions"));
const AdminSermons = lazy(() => import("@/pages/admin/AdminSermons"));
const AdminEvents = lazy(() => import("@/pages/admin/AdminEvents"));
const AdminEventTemplates = lazy(() => import("@/pages/admin/AdminEventTemplates"));
const AdminMinistries = lazy(() => import("@/pages/admin/AdminMinistries"));
const AdminTeam = lazy(() => import("@/pages/admin/AdminTeam"));
const AdminMediaLibrary = lazy(() => import("@/pages/admin/AdminMediaLibrary"));
const AdminPageEditor = lazy(() => import("@/pages/admin/AdminPageEditor"));
const AdminTestimonials = lazy(() => import("@/pages/admin/AdminTestimonials"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminStaff = lazy(() => import("@/pages/admin/AdminStaff"));
const AdminMessageTemplates = lazy(() => import("@/pages/admin/AdminMessageTemplates"));
const AdminLiveEnhanced = lazy(() => import("@/pages/admin/AdminLiveEnhanced"));
const AdminLivePhase2 = lazy(() => import("@/pages/admin/AdminLivePhase2"));
const AdminCampaigns = lazy(() => import("@/pages/admin/AdminCampaigns"));
const SubmissionDetail = lazy(() => import("@/pages/admin/SubmissionDetail"));
const AdminLiveEngagement = lazy(() => import("@/pages/admin/AdminLiveEngagement"));
const AdminPrayerTeam = lazy(() => import("@/pages/admin/AdminPrayerTeam"));
const AdminVolunteers = lazy(() => import("@/pages/admin/AdminVolunteers"));
const AdminQRCodes = lazy(() => import("@/pages/admin/AdminQRCodes"));
const AdminHelp = lazy(() => import("@/pages/admin/AdminHelp"));
const AdminNotificationLogs = lazy(() => import("@/pages/admin/AdminNotificationLogs"));

// Lazy load admin panel pages
const AdminPanelOverview = lazy(() => import("@/pages/admin/panel/AdminPanelOverview"));
const AdminPanelUsers = lazy(() => import("@/pages/admin/panel/AdminPanelUsers"));
const AdminPanelPartners = lazy(() => import("@/pages/admin/panel/AdminPanelPartners"));
const AdminPanelTransactions = lazy(() => import("@/pages/admin/panel/AdminPanelTransactions"));
const AdminPanelKnowledgeBase = lazy(() => import("@/pages/admin/panel/AdminPanelKnowledgeBase"));
const AdminPanelAuditLogs = lazy(() => import("@/pages/admin/panel/AdminPanelAuditLogs"));

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster position="top-right" />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route element={<Layout />}>
              <Route path="/" element={<HomePage />} />
            <Route path="/about-us" element={<AboutUsPage />} />
            <Route path="/leadership" element={<LeadershipPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/next-steps" element={<NextStepsPage />} />
            <Route path="/next-steps-hub" element={<NextStepsHubPage />} />
            {/* Redirect old classes URL to combined next-steps page */}
            <Route path="/next-steps/classes" element={<Navigate to="/next-steps" replace />} />
            <Route path="/plan-of-salvation" element={<PlanOfSalvationPage />} />
            <Route path="/baptism" element={<BaptismPage />} />
            <Route path="/membership" element={<MembershipPage />} />
            <Route path="/partnership" element={<PartnershipPage />} />
            <Route path="/prayer-request" element={<PrayerRequestPage />} />
            <Route path="/plan-visit" element={<PlanYourVisitPage />} />
            <Route path="/volunteer" element={<VolunteerPage />} />
            <Route path="/our-ministries" element={<MinistriesPage />} />
            <Route path="/ministries/:slug" element={<MinistryDetailPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/:slug" element={<EventDetailPage />} />
            <Route path="/sermons" element={<SermonsPage />} />
            <Route path="/sermons/:slug" element={<SermonDetailPage />} />
            <Route path="/onlinegiving" element={<GivingOptionsPage />} />
            <Route path="/giving-options" element={<GivingOptionsPage />} />
            <Route path="/share-testimony" element={<ShareTestimonyPage />} />
            <Route path="/live" element={<LiveWatchPage />} />
            <Route path="/watch" element={<LiveWatchPage />} />
            <Route path="/watch-live" element={<LiveWatchPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/resources/:resourceId" element={<ResourceViewerPage />} />
            <Route path="/connect" element={<ConnectCardPage />} />
            <Route path="/connect-card" element={<ConnectCardPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/safety" element={<SafetyPolicyPage />} />
            <Route path="/safety-policy" element={<SafetyPolicyPage />} />
          </Route>

          {/* Admin Login & Auth (outside AdminLayout) */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/forgot-password" element={<ForgotPassword />} />
          <Route path="/admin/reset-password" element={<ResetPassword />} />

          {/* Admin Routes (protected) */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="submissions" element={<AdminSubmissions />} />
            <Route path="submissions/:id" element={<SubmissionDetail />} />
            <Route path="pages" element={<AdminPageEditor />} />
            <Route path="sermons" element={<AdminSermons />} />
            <Route path="events" element={<AdminEvents />} />
            <Route path="event-templates" element={<AdminEventTemplates />} />
            <Route path="ministries" element={<AdminMinistries />} />
            <Route path="team" element={<AdminTeam />} />
            <Route path="testimonials" element={<AdminTestimonials />} />
            <Route path="staff" element={<AdminStaff />} />
            <Route path="templates" element={<AdminMessageTemplates />} />
            <Route path="campaigns" element={<AdminCampaigns />} />
            <Route path="live-engagement" element={<AdminLiveEngagement />} />
            <Route path="live-enhanced" element={<AdminLiveEnhanced />} />
            <Route path="live-management" element={<AdminLivePhase2 />} />
            <Route path="prayer-team" element={<AdminPrayerTeam />} />
            <Route path="volunteers" element={<AdminVolunteers />} />
            <Route path="qr-codes" element={<AdminQRCodes />} />
            <Route path="notification-logs" element={<AdminNotificationLogs />} />
            <Route path="help" element={<AdminHelp />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="media" element={<AdminMediaLibrary />} />
            
            {/* Admin Panel Routes */}
            <Route path="panel" element={<AdminPanelOverview />} />
            <Route path="panel/users" element={<AdminPanelUsers />} />
            <Route path="panel/partners" element={<AdminPanelPartners />} />
            <Route path="panel/transactions" element={<AdminPanelTransactions />} />
            <Route path="panel/knowledge-base" element={<AdminPanelKnowledgeBase />} />
            <Route path="panel/audit-logs" element={<AdminPanelAuditLogs />} />
          </Route>
          
          {/* 404 Catch-all Route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
